export interface MentionQueryInfo {
    query: string;
    atIndex: number;
}

export interface TrackedMention {
    id: number;
    displayName: string;
    userRole: string | null;
    isSubscriber: boolean;
}

export interface PostMention {
    id: number;
    display_name: string;
    user_role: { name: string } | null;
    type: 'user';
    tag_in_message: string;
    is_subscriber: boolean;
}

// Find an @mention trigger: an @ preceded by whitespace (or start of text) with no spaces before the cursor.
export const getMentionQuery = (text: string, cursorPos: number): MentionQueryInfo | null => {
    const startOfTextOrWhitespace = '(?:^|(?<=\\s))';
    const atSign = '@';
    const queryCharsUntilEnd = '([^\\s]*)$';
    const mentionTrigger = new RegExp(`${startOfTextOrWhitespace}${atSign}${queryCharsUntilEnd}`);

    const match = text.slice(0, cursorPos).match(mentionTrigger);
    return match ? { query: match[1], atIndex: cursorPos - match[0].length } : null;
};

// Convert "@Display Name" occurrences into the [[[user~id]]] tokens the backend expects.
export const buildMessageWithTokens = (text: string, mentions: TrackedMention[]): string => {
    // Sort by displayName length descending to avoid partial replacements.
    const sorted = [...mentions].sort((a, b) => b.displayName.length - a.displayName.length);
    return sorted.reduce(
        (messageToUpdate, mention) => messageToUpdate.split(`@${mention.displayName}`).join(`[[[user~${mention.id}]]]`),
        text
    );
};

export const buildMentionsPayload = (mentions: TrackedMention[]): PostMention[] => {
    const seen = new Set<number>();
    return mentions.reduce<PostMention[]>((acc, mention) => {
        if (seen.has(mention.id)) {
            return acc;
        }
        seen.add(mention.id);
        acc.push({
            id: mention.id,
            display_name: mention.displayName,
            user_role: mention.userRole ? { name: mention.userRole } : null,
            type: 'user',
            tag_in_message: '',
            is_subscriber: mention.isSubscriber
        });
        return acc;
    }, []);
};

// Drop mentions whose "@Display Name" text was deleted from the message.
export const filterStaleMentions = (text: string, mentions: TrackedMention[]): TrackedMention[] =>
    mentions.filter((mention) => text.includes(`@${mention.displayName}`));
